// src/components/ICTSalesCard/ICTSalesCard.jsx

import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchICTSalesCards,
  insertICTSalesCard,
  updateICTSalesCard,
  deleteICTSalesCard,
} from "../../../store/ictSalesCardSlice";
import { fetchTourData } from "../../../store/tourDataSlice";
import { fetchDCData } from "../../../store/dcDataSlice";
import { fetchICTData } from "../../../store/uiSlice";
import { fetchIctCostByIctId } from "../../../store/ictCostSlice";
import { fetchTrainers } from "../../../store/trainersSlice";
import { fetchTrainingCards } from "../../../store/trainingCardsSlice";
import { fetchHoursByICTId } from "../../../store/hoursSlice";
import { fetchEmployees } from "../../../store/empDataSlice";

// NEW: import the slice for miscExpenseData
import { fetchMiscExpenses } from "../../../store/miscExpenseDataSlice"; // adjust the path

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../../Styles/Common.css";

const ICTSalesCard = () => {
  const dispatch = useDispatch();

  // ICT Sales Cards
  const {
    data: salesCards,
    loading: salesLoading,
    error: salesError,
  } = useSelector((state) => state.ictSalesCards);

  // Tour data
  const {
    tourData,
    loading: tourLoading,
    error: tourError,
  } = useSelector((state) => state.tourData);

  const {
    travelData,                    // 👈 defined here
    loading: travelLoading,
    error: travelError,
  } = useSelector((state) => state.travelData);
  
  const {
    accommodations,                // 👈 defined here
    loading: accLoading,
    error: accError,
  } = useSelector((state) => state.accommodations);
  // DC data
  const {
    dcData = [],
    loading: dcLoading,
    error: dcError,
  } = useSelector((state) => state.dcData);

  // ICT data
  const {
    ui: ictData = [],
    loading: ictLoading,
    error: ictError,
  } = useSelector((state) => ({
    ui: state.ui.ictData,
    loading: state.ui.loading,
    error: state.ui.error,
  }));

  // trainers & trainingCards
  const {
    trainers = [],
    loading: trainersLoading,
    error: trainersError,
  } = useSelector((state) => state.trainers);

  const {
    trainingCards = [],
    loading: tcLoading,
    error: tcError,
  } = useSelector((state) => state.trainingCards);

  // Hours stored in a dictionary: hoursByIct
  const {
    hoursByIct = {},
    loading: hoursLoading,
    error: hoursError,
  } = useSelector((state) => state.hours);

  // employees (for trainer name)
  const {
    employees = [],
    loading: empLoading,
    error: empError,
  } = useSelector((state) => state.empData);

  // NEW: miscExpenseData for meCost
  const {
    data: miscExpenses = [],
    loading: meLoading,
    error: meError,
  } = useSelector((state) => state.miscExpenseData);

  // local states
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // form popup
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const [formData, setFormData] = useState({
    id: "",
    ict_id: "",
    tourBills: "",
    travelBills: "",
    accommodationBills: "",
    trainer_id: "",
    trainingCard_id: "",
    dcLab_id: "",
    status: "A",
    created: "",
  });

  // Fetch data on mount
  useEffect(() => {
    dispatch(fetchICTSalesCards());
    dispatch(fetchTourData());
    dispatch(fetchDCData());
    dispatch(fetchICTData());
    dispatch(fetchTrainers());
    dispatch(fetchTrainingCards());
    dispatch(fetchEmployees());

    // NEW: also fetch miscExpenses
    dispatch(fetchMiscExpenses());
  }, [dispatch]);

  // For each row, fetch hours by ict_id
  useEffect(() => {
    salesCards.forEach((card) => {
      if (card.ict_id) {
        dispatch(fetchHoursByICTId(card.ict_id));
      }
    });
  }, [salesCards, dispatch]);

  // Filtering & pagination
  const filteredSalesCards = salesCards.filter((card) => {
    const idMatch = card.id.toString().includes(searchTerm);
    const statusMatch = card.status?.toLowerCase().includes(searchTerm.toLowerCase());
    return idMatch || statusMatch;
  });
  const totalPages = Math.ceil(filteredSalesCards.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredSalesCards.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (page) => setCurrentPage(page);

  // Basic form handlers
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleAdd = () => {
    setEditMode(false);
    setIsFormOpen(true);
    setFormData({
      id: "",
      ict_id: "",
      tourBills: "",
      travelBills: "",
      accommodationBills: "",
      trainer_id: "",
      trainingCard_id: "",
      dcLab_id: "",
      status: "A",
      created: "",
    });
  };

  const handleEdit = (card) => {
    setEditMode(true);
    setIsFormOpen(true);

    // Optionally auto-fill "tourBills" from matching tours
    const matchedTours = (tourData || []).filter(
      (t) => Number(t.tc_id) === Number(card.trainingCard_id)
    );
    const totalTourCost = matchedTours.reduce((sum, t) => sum + Number(t.tourCost || 0), 0);

    setFormData({
      ...card,
      tourBills: totalTourCost.toString() || card.tourBills,
    });
  };

  const closeForm = () => setIsFormOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editMode) {
        await dispatch(updateICTSalesCard(formData)).unwrap();
        toast.success("ICT Sales Card updated!");
      } else {
        await dispatch(insertICTSalesCard(formData)).unwrap();
        toast.success("ICT Sales Card created!");
      }
      closeForm();
    } catch (err) {
      toast.error("Failed to save ICT Sales Card.");
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this record?")) {
      try {
        await dispatch(deleteICTSalesCard(id)).unwrap();
        toast.success("Record deleted!");
      } catch (err) {
        toast.error("Failed to delete record.");
      }
    }
  };

  const handleToggleStatus = async (card) => {
    const newStatus = card.status === "A" ? "D" : "A";
    const updatedCard = { ...card, status: newStatus };
    try {
      await dispatch(updateICTSalesCard(updatedCard)).unwrap();
      toast.success(`Status updated to ${newStatus === "A" ? "Active" : "Deactivated"}`);
    } catch (err) {
      toast.error("Failed to update status.");
    }
  };

  // Instead of a popup, open a new tab to /ict-sales-card/receipt/:id
  const handleReceipt = (card) => {
    // Optionally fetch cost details first
    if (card.ict_id) {
      dispatch(fetchIctCostByIctId(card.ict_id));
    }
    window.open(`/ict-sales-card/receipt/${card.id}`, "_blank");
  };

  // utility function to find base trainer cost
  const getBaseTrainerCost = (trainerId, trainingCardId) => {
    const trainerRec = trainers.find((t) => Number(t.id) === Number(trainerId));
    if (!trainerRec) return 0;

    const tCard = trainingCards.find((tc) => Number(tc.id) === Number(trainingCardId));
    if (!tCard) return 0;

    const mode = tCard.ictStudyMode || "";
    if (mode === "Online") {
      return trainerRec.trainerCostOnline || 0;
    } else if (mode === "ClassRoom") {
      return trainerRec.trainerCostClassRoom || 0;
    }
    return 0;
  };

  // sum of training+lab hours
  const getICTDuration = (ict_id) => {
    let total = 0;
    const hoursArr = hoursByIct[ict_id] || [];
    hoursArr.forEach((h) => {
      total += Number(h.TrainingHour || 0);
      total += Number(h.LabHour || 0);
    });
    return total;
  };

  // total trainer cost => baseTrainerCost * ( getICTDuration(ict_id) / 8 )
  const getTotalTrainerCost = (card) => {
    const baseCost = getBaseTrainerCost(card.trainer_id, card.trainingCard_id);
    const duration = getICTDuration(card.ict_id);
    return baseCost * (duration / 8);
  };

  // display trainer name
  const getTrainerDisplayName = (trainerId) => {
    const trainerRec = trainers.find((t) => Number(t.id) === Number(trainerId));
    if (!trainerRec) return `T#${trainerId}`;
    const emp = employees.find((e) => Number(e.id) === Number(trainerRec.empId));
    if (!emp) return `T#${trainerId}`;
    return `${emp.empFName} ${emp.empLName} (${trainerId})`;
  };

  // NEW: sum up meCost from the miscExpenses for the matching tc_id
  const getMiscExpenseSum = (tcId) => {
    // Filter all miscExpenses for the matching trainingCard
    const matched = miscExpenses.filter((m) => Number(m.tc_id) === Number(tcId));
    // Sum up meCost
    const total = matched.reduce((sum, rec) => sum + Number(rec.meCost || 0), 0);
    return total;
  };
  const getTourCostSum = tcId =>
    (tourData ?? [])
      .filter(t => Number(t.tc_id) === Number(tcId))
      .reduce((sum, rec) => sum + Number(rec.tourCost ?? 0), 0);
  
  const getTravelCostSum = tcId =>
    (travelData ?? [])
      .filter(tr => Number(tr.tc_id) === Number(tcId))
      .reduce((sum, rec) => sum + Number(rec.travelCost ?? 0), 0);
  
  const getAccommodationCostSum = tcId =>
    (accommodations ?? [])
      .filter(a => Number(a.tc_id) === Number(tcId))
      .reduce((sum, rec) => sum + Number(rec.accommodationCost ?? 0), 0);
  

  return (
    <div className="container">
      <div className="header-container">
        <h2>ICT Sales Cards</h2>
        <button className="add-lead-btn" onClick={handleAdd}>
          <i className="fa fa-plus"></i>
        </button>
      </div>

      <div className="controls">
        <input
          type="text"
          placeholder="Search by ID or Status"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="filter-input"
        />
      </div>

      {/* combined loading */}
      {(salesLoading ||
        tourLoading ||
        travelLoading ||
        accLoading ||
        dcLoading ||
        ictLoading ||
        trainersLoading ||
        tcLoading ||
        hoursLoading ||
        empLoading ||
        meLoading) && <p>Loading data...</p>}

      {/* combined errors */}
      {(salesError ||
        tourError ||
        dcError ||
        ictError ||
        trainersError ||
        tcError ||
        hoursError ||
        empError ||
        meError) && (
        <p className="error">
          {salesError ||
            tourError ||
            travelError ||
            accError ||
            dcError ||
            ictError ||
            trainersError ||
            tcError ||
            hoursError ||
            empError ||
            meError}
        </p>
      )}

      <table className="table">
        <thead>
          <tr>
            <th>#</th>
            <th>ICT Name</th>
            <th>Tour Bills</th>
            <th>Travel Bills</th>
            <th>Stay Bills</th>
            <th>Misc Expense</th> {/* new column for sum of meCost */}
            <th>Trainer</th>
            <th>Base Trainer Cost</th>
            <th>Total Trainer Cost</th>
            <th>Training Card ID</th>
            <th>DC Cost</th>
            <th>Status</th>
            <th>Created</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentItems.map((card, index) => {
            // find ICT name
            const ictRec = ictData.find((i) => i.id === card.ict_id);
            const ictName = ictRec ? ictRec.ictName : `ICT#${card.ict_id}`;

            // trainer cost
            const baseCost = getBaseTrainerCost(card.trainer_id, card.trainingCard_id);
            const totalTrainerCost = getTotalTrainerCost(card);

            // DC cost
            let dcCost = 0;
            if (card.dcLab_id) {
              const matchedDC = dcData.find((dc) => dc.id === card.dcLab_id);
              if (matchedDC) dcCost = matchedDC.dcCost || 0;
            }

            // new: sum of meCost from miscExpenses
            const miscExpenseSum = getMiscExpenseSum(card.trainingCard_id);

            const trainerDisplay = card.trainer_id
              ? getTrainerDisplayName(card.trainer_id)
              : "-";

            return (
              <tr key={card.id}>
                <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                <td>{ictName}</td>
                <td>{getTourCostSum(card.trainingCard_id || 0)}</td>
                <td>{getTravelCostSum(card.trainingCard_id || 0)}</td>
                <td>{getAccommodationCostSum(card.trainingCard_id || 0)}</td>
                {/* Display sum of meCost from the matching trainingCard_id */}
                <td>{miscExpenseSum}</td>
                <td>{trainerDisplay}</td>
                <td>{baseCost}</td>
                <td>{totalTrainerCost}</td>
                <td>{card.trainingCard_id}</td>
                <td>{dcCost}</td>
                <td>
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={card.status === "A"}
                      onChange={() => handleToggleStatus(card)}
                    />
                    <span className="slider round"></span>
                  </label>
                </td>
                <td>
                  {card.created ? new Date(card.created).toLocaleString() : "-"}
                </td>
                <td style={{ whiteSpace: "nowrap" }}>
                  <button
                    className="icon-btn update-btn"
                    onClick={() => handleEdit(card)}
                  >
                    <i className="fa fa-edit" aria-hidden="true"></i>
                  </button>
                  <button
                    className="icon-btn delete-btn"
                    onClick={() => handleDelete(card.id)}
                  >
                    <i className="fa fa-trash" aria-hidden="true"></i>
                  </button>
                  <i
                    className="fa fa-eye icon-btn"
                    style={{ marginLeft: "0.5rem", cursor: "pointer" }}
                    title="View Receipt"
                    onClick={() => handleReceipt(card)}
                  ></i>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          {Array.from({ length: totalPages }, (_, i) => (
            <button
              key={i + 1}
              className={currentPage === i + 1 ? "active" : ""}
              onClick={() => handlePageChange(i + 1)}
            >
              {i + 1}
            </button>
          ))}
        </div>
      )}

      {/* Add/Edit Popup */}
      {isFormOpen && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h3>{editMode ? "Edit ICT Sales Card" : "Add ICT Sales Card"}</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>ICT ID</label>
                <input
                  type="number"
                  name="ict_id"
                  value={formData.ict_id}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Tour Bills</label>
                <input
                  type="number"
                  step="0.01"
                  name="tourBills"
                  value={formData.tourBills}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Travel Bills</label>
                <input
                  type="number"
                  step="0.01"
                  name="travelBills"
                  value={formData.travelBills}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Accommodation Bills</label>
                <input
                  type="number"
                  step="0.01"
                  name="accommodationBills"
                  value={formData.accommodationBills}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Trainer ID</label>
                <input
                  type="number"
                  name="trainer_id"
                  value={formData.trainer_id}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Training Card ID</label>
                <input
                  type="number"
                  name="trainingCard_id"
                  value={formData.trainingCard_id}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>DC Lab ID</label>
                <input
                  type="number"
                  name="dcLab_id"
                  value={formData.dcLab_id}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value="A">Active (A)</option>
                  <option value="D">Deactivated (D)</option>
                </select>
              </div>
              <div className="form-actions">
                <button type="submit" className="fa fa-check submit-icon"></button>
                <button
                  type="button"
                  className="fa fa-times cancel-icon"
                  onClick={closeForm}
                ></button>
              </div>
            </form>
          </div>
        </div>
      )}

      <ToastContainer position="bottom-right" autoClose={3000} />
    </div>
  );
};

export default ICTSalesCard;
